// medplum-portal.js — ONLINE-PRIMARY patient-portal / post-visit-summary data path (main-2, CARD 5).
//
// The portal (/portal, /summary/:id — Portal.jsx re-exports Summary.jsx) is an ANONYMOUS,
// magic-link-token-gated surface: there is no per-user Medplum Bearer token, so it CANNOT use the
// FHIR-direct client-shaping path the staff screens use (getMedplum). Instead it reaches Medplum
// through the existing same-origin portal gateway. That gateway verifies the
// magic link, derives the authorized Encounter/Patient, and only then invokes
// an exactly-pinned Bot with credential-free input. Medplum archives Bot inputs,
// so reusable magic-link credentials must terminate at the gateway.
//
// SCOPE (design-anon.md CARD 5 + the 2026-07-03 owner-decided grant model): this module wraps the
// read (orders) and the who-has-access registry +
// patient revoke (the access-grant Bot — the old "deliberately left on Express /access/revoke"
// hold is lifted now that the owner decided the grant model; truth = Patient.meta.accounts +
// grant/revoke AuditEvents, enforced by RLS). Deliberately NOT here:
//   - report upload — the magic-link-gated Express gateway transaction-creates
//     Binary + DocumentReference because a Bot cannot accept a 15MB multipart body.
//   - the prescription PDF — a binary stream the browser navigates to directly, not a JSON $operation.
//
// executeBot returns the handler value either directly or wrapped in { returnValue } — unwrap both
// (the same defensive pattern medplum-writes.js uses).
import { apiUrl } from './net-base.js';
import { runChartReadBot } from './medplum-intake.js';
import { buildEncounterView } from './shape/encounter-view.js';

const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';

async function portalJson(path, { token, method = 'GET', body } = {}) {
  const sep = path.includes('?') ? '&' : '?';
  const url = apiUrl(`${path}${sep}token=${encodeURIComponent(token || '')}`);
  const response = await fetch(url, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(out.error || `portal request failed (${response.status})`);
  return out;
}

// Reads this encounter's lab orders + slips via the opd-portal-orders Bot. Returns the EXACT shape
// the Express GET /portal/orders returned ({ ok, encounterId, orders, slips }) so Summary.jsx
// OrdersCard.normalizePortalOrders is unchanged. Throws (fail-loud) on an unauthorized token or a
// transport error — the caller (OrdersCard) already treats a rejection as "no orders / hide card".
export async function loadPortalOrders(token) {
  return portalJson('/api/v1/opd/portal/orders', { token });
}

// ── WHO-HAS-ACCESS registry + patient revoke (the access-grant Bot) ─────────
// Both preserve the SAME access-grant Bot contract used by staff, but the
// browser calls the magic-link gateway. The body-supplied patientId remains in
// the public function signature for UI compatibility and is deliberately not
// trusted or transmitted; the gateway derives it from the token's Encounter.

function accessBotResult(r, what) {
  // Fail-loud unwrap shared by both calls: an unauthorized token, a refused
  // mode, or a Bot-side failure all throw with the Bot's own reason — never a
  // silent partial result.
  if (!r || r.authorized === false || r.ok === false) {
    const reason = (r && (r.reason || r.error)) || 'unauthorized';
    throw new Error(what + ' failed: ' + reason);
  }
  return r;
}

// Reads the patient's own access registry: Patient.meta.accounts (who holds a
// compartment stamp today) + recent grant/revoke AuditEvents. Returns the Bot
// contract shape { ok, accounts: [{ ref, display, type }], events: [{ when,
// who, whoDisplay, kind }] }. Throws fail-loud on a bad token / transport error.
export async function loadAccessRegistry({ patientId, magicToken } = {}) {
  void patientId;
  const r = await portalJson('/api/v1/opd/portal/access-registry', { token: magicToken });
  return accessBotResult(r, 'access registry');
}

// One-tap patient revoke of a grantee (Practitioner or Organization ref) on the
// patient's OWN record: the Bot un-stamps granteeRef from the compartment with
// propagate and writes the revoke AuditEvent. Throws fail-loud on refusal.
export async function revokeAccessFromPortal({ patientId, granteeRef, magicToken } = {}) {
  void patientId;
  const r = await portalJson('/api/v1/opd/portal/access-revoke', {
    token: magicToken,
    method: 'POST',
    body: { granteeRef },
  });
  return accessBotResult(r, 'access revoke'); // { ok, mode:'revoke', grantee }
}

// ── PORTAL / POST-VISIT-SUMMARY BOOT LOADER (main-2, Wave-3A) ────────────────
// Replaces the Express GET /view/bootstrap.js?route=portal READ leg for
// /portal, /summary and /summary/:id. The portal is anonymous + magic-link-gated, so it
// CANNOT use the staff FHIR-direct path — it reuses the SAME blessed chart-read
// Bot the intake surfaces use (executed as the kioskChartRead anon client),
// shapes the raw graph with buildEncounterView(), and adds the two portal-only
// extras Summary.jsx pick-reads: portal.portal.pdfUrl and portal.portal.referral.
// This mirrors the server's buildPortalView: { ...encounterView, portal: {…} },
// installed under __OPD_DATA__.portal so Summary reads 'portal.patient.*',
// 'portal.consult.*', 'portal.intake', 'portal.portal.pdfUrl', etc.

function healtherBlob(resource) {
  const ext = (resource && resource.extension) || [];
  const blob = ext.find((e) => e && e.url === EXT_OPD_HEALTHER && e.valueString);
  if (!blob) return {};
  try { return JSON.parse(blob.valueString) || {}; } catch (_) { return {}; }
}

// Decode the magic-link token's expiry (base64url payload.exp) for parity with
// the server's portal.tokenExp — best-effort, null when the token is unreadable.
function tokenExpFromToken(token) {
  try {
    const enc = String(token || '').split('.')[0];
    if (!enc) return null;
    let s = enc.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    const payload = JSON.parse(atob(s));
    return payload && payload.exp ? payload.exp : null;
  } catch (_) { return null; }
}

// Outgoing referral created FROM this consult (referrals.mjs stores it as a
// ServiceRequest on the ORIGIN encounter with _healther.kind==='referral' /
// category text 'referral'). The chart-read Bot's graph carries it via the
// ServiceRequest _revinclude, so we shape it client-side. Returns null when
// there is no outgoing referral (the common case — the quiet line stays hidden).
// NOTE: the referral's queue token lives on the TARGET encounter, which is not
// in this single-encounter graph — surfaced from the _healther blob when the
// mirror preserved it, else null (department/doctor/priority/status still show).
function referralFromGraph(graph) {
  const srs = (graph && graph.serviceRequests) || [];
  const referrals = srs.filter((sr) => {
    if (!['active', 'on-hold', 'completed'].includes(sr.status)) return false;
    const h = healtherBlob(sr);
    if (h.kind === 'referral') return true;
    const cat = ((sr.category || [])[0] || {});
    return String(cat.text || '').toLowerCase() === 'referral';
  });
  if (!referrals.length) return null;
  const sr = referrals[referrals.length - 1];
  const h = healtherBlob(sr);
  return {
    department: h.toDepartmentName || (sr.performer && sr.performer[0] && sr.performer[0].display) || '',
    doctor: h.toDoctor || (sr.performer && sr.performer[0] && sr.performer[0].display) || null,
    token: h.referralToken || h.token || null,
    priority: h.priority || sr.priority || 'routine',
    status: sr.status || 'active',
  };
}

export async function loadPortalBoot({ id = '', token = '', lang = '' } = {}) {
  // The bare /summary route is an intentional no-visit landing state. Do not
  // call the chart-read Bot with no authority or encounter to resolve and then
  // mislabel its expected `missing` refusal as a broken link.
  if (!id && !token) {
    return {
      meta: {
        route: 'portal',
        encounterId: null,
        token: null,
        lang: lang || (() => { try { return localStorage.getItem('healther.opd.lang') || 'en'; } catch (_) { return 'en'; } })(),
        now: new Date().toISOString(),
      },
      portal: null,
      hospital: {},
      currentUser: null,
      features: { rppg: false },
    };
  }
  let result;
  try {
    result = await runChartReadBot({ token: token || undefined, encounterId: id || undefined });
  } catch (error) {
    // A Bot-refused token is an expected, displayable domain outcome. This is
    // not an alternate data path: the portal remains empty and carries only the
    // refusal reason. Network/configuration failures still throw fail-loud.
    if (!error || error.unauthorized !== true) throw error;
    return {
      meta: {
        route: 'portal',
        encounterId: id || null,
        token: token || null,
        lang: lang || (() => { try { return localStorage.getItem('healther.opd.lang') || 'en'; } catch (_) { return 'en'; } })(),
        now: new Date().toISOString(),
        portalLinkError: error.reason || 'unauthorized',
      },
      portal: null,
      hospital: {},
      currentUser: null,
      features: { rppg: false },
    };
  }
  const { r, cfg } = result;
  const view = buildEncounterView(r.graph);
  const encId = r.encounterId || (view.encounter && view.encounter.id) || id || null;
  const signed = !!view.visitSummary;
  const finalArtifactError = !signed && (
    r.graph?.encounter?.status === 'finished'
    || ['PRESCRIBED', 'COMPLETED'].includes(view.encounter?.state)
  )
    ? 'missing-final-visit-summary'
    : null;
  return {
    meta: {
      route: 'portal',
      encounterId: encId,
      token: token || null,
      lang: lang || (() => { try { return localStorage.getItem('healther.opd.lang') || 'en'; } catch (_) { return 'en'; } })(),
      now: new Date().toISOString(),
    },
    portal: {
      ...view,
      // Patient-facing clinical content is frozen at sign. Never read it from
      // the mutable preliminary consult-draft Composition after signing.
      draft: signed ? view.signedDraft : {},
      consult: { ...view.consult, signed },
      finalArtifactError,
      // The portal-only extras (server buildPortalView). accessRegistry is loaded
      // LIVE by AccessRegistryCard (loadAccessRegistry) so it is null at boot;
      // pdfUrl + referral are the fields Summary.jsx pick-reads off portal.portal.
      portal: {
        tokenExp: tokenExpFromToken(token),
        pdfUrl: apiUrl(`/api/v1/opd/portal/prescription/pdf?token=${encodeURIComponent(token || '')}`),
        referral: referralFromGraph(r.graph),
        accessRegistry: null,
      },
    },
    hospital: (cfg && cfg.hospital) || {},
    currentUser: null,
    features: { rppg: false },
  };
}
